import * as Sentry from '@sentry/react'
import { SiteType } from '../openapi'

interface UserInfo {
  id: number | string
  nickname?: string | null
  membershipType?: string | null
  siteName: string
}

// Cache the last set user to avoid redundant Sentry calls
let lastSetUserId: string | null = null

/**
 * Set user information in Sentry for better error tracking
 * Optimized to prevent redundant API calls
 */
export function setSentryUser(user: UserInfo, siteName: SiteType): void {
  const userIdStr = user.id.toString()

  // Skip if we're setting the same user again
  if (lastSetUserId === userIdStr) {
    return
  }

  lastSetUserId = userIdStr

  // Set user information in Sentry
  Sentry.setUser({
    id: userIdStr,
    username: user.nickname || 'Anonymous Guest',
    email: `${user.siteName}-${userIdStr}`,
  })

  // Add tags for filtering
  const siteNameStr = siteName === SiteType.Online ? 'online' : 'license'
  Sentry.setTag('site_name', siteNameStr)
  Sentry.setTag(
    'user_type',
    siteName === SiteType.Online ? 'online_user' : 'license_user',
  )

  if (user.membershipType) {
    Sentry.setTag('membership_type', user.membershipType)
  }

  // Add context with full user details
  Sentry.setContext('digitane', {
    userId: userIdStr,
    nickname: user.nickname || 'Anonymous',
    siteName: siteNameStr,
    membershipType: user.membershipType || 'unknown',
    env: import.meta.env.MODE || 'production',
  })
}

/**
 * Clear user information from Sentry
 */
export function clearSentryUser(): void {
  // Only clear if there was a user set
  if (lastSetUserId === null) {
    return
  }

  lastSetUserId = null
  Sentry.setUser(null)
  Sentry.setTag('site_name', undefined)
  Sentry.setTag('user_type', undefined)
  Sentry.setTag('membership_type', undefined)
  Sentry.setContext('digitane', null)
}
